// Pure rig math for MechView (no Phaser): angle helpers, facing conversions and
// a two-bone IK solver for the arms. Screen space: y down, angles in radians,
// 0 = right, positive = clockwise.

export const TAU = Math.PI * 2;

/** Wrap to (-π, π]. */
export function wrapAngle(a: number): number {
  a = a % TAU;
  if (a <= -Math.PI) a += TAU;
  else if (a > Math.PI) a -= TAU;
  return a;
}

/** Shortest-arc interpolation between two angles. */
export function lerpAngle(a: number, b: number, t: number): number {
  return a + wrapAngle(b - a) * t;
}

/**
 * World angle → the right-facing local frame of a mech that faces `facing`.
 * Mirroring x maps a direction θ to π − θ.
 */
export function toLocalAngle(world: number, facing: 1 | -1): number {
  return facing === 1 ? wrapAngle(world) : wrapAngle(Math.PI - world);
}

/** Local (right-facing) angle → world angle for `facing` (the mirror is its own inverse). */
export function toWorldAngle(local: number, facing: 1 | -1): number {
  return toLocalAngle(local, facing);
}

export interface TwoBoneResult {
  /** Absolute angle of the first bone (shoulder → elbow). */
  upper: number;
  /** Absolute angle of the second bone (elbow → hand). */
  fore: number;
  /** Elbow position. */
  ex: number;
  ey: number;
  /** Hand position actually reached (clamped toward the target when out of reach). */
  hx: number;
  hy: number;
  reached: boolean;
}

export function twoBoneResult(): TwoBoneResult {
  return { upper: 0, fore: 0, ex: 0, ey: 0, hx: 0, hy: 0, reached: true };
}

/**
 * Two-bone IK from a root (sx, sy) toward a target (tx, ty) with bone lengths
 * l1, l2. bend = +1 puts the elbow on the clockwise side of the root→target
 * line (below it for a target to the right: a natural "elbow down" arm).
 * Unreachable targets are clamped: the chain points at the target, fully
 * extended (or folded to the minimum reach). Writes into `out` (no allocation).
 */
export function solveTwoBone(
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  l1: number,
  l2: number,
  bend: 1 | -1,
  out: TwoBoneResult,
): TwoBoneResult {
  const dx = tx - sx;
  const dy = ty - sy;
  const dist = Math.hypot(dx, dy);
  const base = dist > 1e-6 ? Math.atan2(dy, dx) : 0;
  const maxReach = Math.max(1e-3, l1 + l2 - 1e-3);
  const minReach = Math.abs(l1 - l2) + 1e-3;
  const d = Math.min(maxReach, Math.max(minReach, dist));
  out.reached = dist <= l1 + l2 + 1e-3 && dist >= Math.abs(l1 - l2) - 1e-3;
  const cosA = (l1 * l1 + d * d - l2 * l2) / (2 * l1 * d);
  const alpha = Math.acos(Math.max(-1, Math.min(1, cosA)));
  out.upper = base + bend * alpha;
  out.ex = sx + Math.cos(out.upper) * l1;
  out.ey = sy + Math.sin(out.upper) * l1;
  // Hand on the (clamped) target circle along the root→target direction.
  const hx = sx + Math.cos(base) * d;
  const hy = sy + Math.sin(base) * d;
  out.fore = Math.atan2(hy - out.ey, hx - out.ex);
  out.hx = out.ex + Math.cos(out.fore) * l2;
  out.hy = out.ey + Math.sin(out.fore) * l2;
  return out;
}

/** Forward kinematics: hand position for absolute bone angles. */
export function forwardKinematics(
  sx: number,
  sy: number,
  upper: number,
  fore: number,
  l1: number,
  l2: number,
  out: TwoBoneResult,
): TwoBoneResult {
  out.upper = upper;
  out.fore = fore;
  out.ex = sx + Math.cos(upper) * l1;
  out.ey = sy + Math.sin(upper) * l1;
  out.hx = out.ex + Math.cos(fore) * l2;
  out.hy = out.ey + Math.sin(fore) * l2;
  out.reached = true;
  return out;
}

/** Rotate (x, y) by angle a. */
export function rotX(x: number, y: number, a: number): number {
  return x * Math.cos(a) - y * Math.sin(a);
}

export function rotY(x: number, y: number, a: number): number {
  return x * Math.sin(a) + y * Math.cos(a);
}
